import React, { memo, useCallback, useRef, useEffect } from 'react';
import { EditorStarterItem } from '../../items/item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { NumberControlUpdateHandler } from '@/features/remotion-editor/editor/inspector/controls/number-controls';
import { useTranslations } from 'next-intl';
import { Opacity } from '@crabs-ai/web-icons';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTextSelection } from '../../contexts/text-selection-context';
import { TextItem } from '../../items/text/text-item-type';

/**
 * TextItem에서 opacity 값을 추출 (segments 고려)
 * Mixed 상태면 -1 반환
 */
const getOpacityFromTextItem = (item: EditorStarterItem): number => {
  if (item.type !== 'text') {
    return item.opacity;
  }

  const textItem = item as TextItem;
  if (!textItem.segments || textItem.segments.length === 0) {
    return textItem.opacity;
  }

  // segments의 opacity를 추출 (없으면 item.opacity 사용)
  const opacities = textItem.segments.map((s) => s.opacity ?? textItem.opacity);

  // 모든 opacity가 같은지 확인
  const firstOpacity = opacities[0]!;
  const allSame = opacities.every((o) => o === firstOpacity);
  if (allSame) {
    return firstOpacity;
  }

  // Mixed 상태 - 특수 값 반환
  return -1;
};

// 선택 영역 내 Mixed 마커
const SELECTION_MIXED = '__SELECTION_MIXED__';

const OpacityControlsUnmemoized: React.FC<{
  opacity: number;
  itemId: string;
  item?: TextItem;
  disableCharacterStyleEditing?: boolean;
  isShowLabel?: boolean;
  isShowLeadingNode?: boolean;
}> = ({
  opacity,
  itemId,
  item,
  disableCharacterStyleEditing = false,
  isShowLabel = true,
  isShowLeadingNode = true,
}) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const { selectionStyle, applyOpacity } = useTextSelection();

  // 선택 상태 (disableCharacterStyleEditing이 true면 항상 false)
  const effectiveHasSelection = disableCharacterStyleEditing
    ? false
    : selectionStyle.hasSelection;

  const targetIds = useTargetItemIds(itemId);
  const { isMixed: isMultiItemMixed, value: selectedOpacity } =
    useSelectionValues<EditorStarterItem, number>(
      itemId,
      getOpacityFromTextItem,
    );

  // 최종 Mixed 여부 결정
  const isMixed = effectiveHasSelection
    ? selectionStyle.opacity === SELECTION_MIXED
    : isMultiItemMixed || selectedOpacity === -1;

  const setOpacity = useCallback(
    (newOpacity: number, commitToUndoStack: boolean) => {
      // 텍스트 선택이 있었으면 선택 영역에만 적용
      if (effectiveHasSelection) {
        applyOpacity(newOpacity);
        return;
      }

      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const prev = i as EditorStarterItem;
            if (prev.opacity === newOpacity) {
              return prev;
            }

            // TextItem인 경우 segments에서 opacity 제거
            if (prev.type === 'text') {
              const textItem = prev as TextItem;
              let newSegments = textItem.segments?.map((seg) => {
                const { opacity: _o, ...rest } = seg;
                return rest;
              });
              // 모든 segment가 빈 객체(text만 있는)면 segments 제거
              const hasStyledSegments = newSegments?.some(
                (s) =>
                  s.color ||
                  s.strokeColor ||
                  s.fontFamily ||
                  s.fontWeight ||
                  s.fontVariant ||
                  s.fontSize,
              );
              if (!hasStyledSegments) {
                newSegments = undefined;
              }
              return {
                ...prev,
                opacity: newOpacity,
                segments: newSegments,
              };
            }

            return {
              ...prev,
              opacity: newOpacity,
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds, applyOpacity],
  );

  const handleSliderChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      const newOpacity = value / 100;
      setOpacity(newOpacity, commitToUndoStack);
    },
    [setOpacity],
  );

  const opacityPercent = Math.round(opacity * 100);

  // 표시할 opacity 결정 (선택 영역의 opacity 반영)
  const displayOpacityPercent = effectiveHasSelection
    ? selectionStyle.opacity === SELECTION_MIXED
      ? opacityPercent
      : selectionStyle.opacity
        ? Math.round(parseFloat(selectionStyle.opacity) * 100)
        : opacityPercent
    : opacityPercent;

  const onValueChange: NumberControlUpdateHandler = useCallback(
    (update) => {
      handleSliderChange(update.num, update.commitToUndoStack);
    },
    [handleSliderChange],
  );

  // +/- 버튼 디바운스 타이머
  const commitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingOpacityRef = useRef<number | null>(null);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }
    };
  }, []);

  // 디바운스된 commit 함수
  const debouncedCommit = useCallback(
    (newOpacityPercent: number) => {
      // 이전 타이머 취소
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }

      // character-level: 즉시 적용 (Lexical 자체 히스토리 사용)
      if (effectiveHasSelection) {
        applyOpacity(newOpacityPercent / 100);
        return;
      }

      // box-level: 즉시 적용 (undo stack에 저장 안 함)
      handleSliderChange(newOpacityPercent, false);
      pendingOpacityRef.current = newOpacityPercent;

      // 500ms 후에 undo stack에 저장
      commitTimeoutRef.current = setTimeout(() => {
        if (pendingOpacityRef.current !== null) {
          handleSliderChange(pendingOpacityRef.current, true);
          pendingOpacityRef.current = null;
        }
      }, 500);
    },
    [applyOpacity, handleSliderChange],
  );

  // +/- 버튼 핸들러
  const handleIncrement = useCallback(() => {
    const newOpacity = Math.min(100, displayOpacityPercent + 1);
    debouncedCommit(newOpacity);
  }, [displayOpacityPercent, debouncedCommit]);

  const handleDecrement = useCallback(() => {
    const newOpacity = Math.max(0, displayOpacityPercent - 1);
    debouncedCommit(newOpacity);
  }, [displayOpacityPercent, debouncedCommit]);

  return (
    <div className="flex-1">
      <ValueInputField
        isUnit="%"
        isShowLabel={isShowLabel}
        label={t('Editor.inspector.opacity')}
        isShowLeadingNode={isShowLeadingNode}
        leadingNodeType="icon"
        leadingNodeValue={
          <Opacity className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
        }
        setValue={onValueChange}
        value={opacityPercent}
        min={0}
        max={100}
        step={1}
        accessibilityLabel="Opacity"
        hasMixedValue={isMixed}
        className="w-full"
      />
    </div>
  );
};

export const OpacityControls = memo(OpacityControlsUnmemoized);
